import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout, settleWithTimeout } from '../lib/supabaseRead';
import { showToast } from '../lib/toast';
import { trackAppEvent } from '../lib/analytics';
import { reportClientDiagnostic } from '../lib/clientDiagnostics';

export function usePipeline(setTracked) {
  const [loading, setLoading] = useState(false);

  const updateStage = useCallback(async (trackedId, fromStage, toStage, extraFields = {}) => {
    if (!trackedId) return;
    setLoading(true);
    const now = new Date().toISOString();
    const updates = { pipeline_stage: toStage, pipeline_updated_at: now, ...extraFields };
    if (toStage === 'contacted' && updates.last_contacted_at === undefined) {
      updates.last_contacted_at = now;
    }
    if (['won', 'lost', 'not_relevant'].includes(toStage)) {
      if (updates.next_follow_up_at === undefined) updates.next_follow_up_at = null;
      if (updates.follow_up_reason === undefined) updates.follow_up_reason = null;
    }
    let previousRow = null;

    // Optimistic update
    setTracked(prev => prev.map(da => {
      if (da.tracked_id !== trackedId) return da;
      previousRow = da;
      return { ...da, ...updates };
    }));

    try {
      const { error } = await readWithTimeout(
        signal => supabase
          .from('tracked_das')
          .update(updates)
          .eq('id', trackedId)
          .abortSignal(signal),
        { label: 'pipeline_stage_update' }
      );

      if (error) throw error;

      // Log history (timeout-guarded so a wedged auth read can't freeze the button)
      const { data } = await settleWithTimeout(supabase.auth.getUser(), { timeoutMs: 4000, label: 'pipeline_get_user' });
      const user = data?.user || null;
      if (user) {
        supabase.from('pipeline_history').insert({
          tracked_da_id: trackedId,
          user_id:       user.id,
          from_stage:    fromStage,
          to_stage:      toStage,
          notes:         extraFields.follow_up_reason || extraFields.lost_reason || null,
          metadata:      extraFields,
        }).then(({ error: historyError }) => {
          if (historyError) console.warn('[usePipeline] pipeline_history insert failed:', historyError.message);
        });
      }
      trackAppEvent('pipeline_stage_update', {
        tracked_da_id: trackedId,
        from_stage: fromStage,
        to_stage: toStage,
        extra_fields: Object.keys(extraFields || {}),
      }, { page: 'tracked', source: 'pipeline_stage_selector', tracked_da_id: trackedId });
    } catch (err) {
      console.error('[pipeline] update failed:', err.message);
      showToast('Could not update pipeline stage');
      let user = null;
      try {
        const { data } = await supabase.auth.getUser();
        user = data?.user || null;
      } catch {
        // Diagnostics are best-effort.
      }
      reportClientDiagnostic('pipeline_stage_update_failed', {
        severity: 'error',
        user,
        surface: 'pipeline',
        page: 'tracked',
        da: previousRow,
        message: err.message || 'Could not update pipeline stage',
        metadata: {
          tracked_id: trackedId,
          from_stage: fromStage,
          to_stage: toStage,
          extra_fields: Object.keys(extraFields || {}),
        },
        error: err,
      });
      // Rollback
      setTracked(prev => prev.map(da =>
        da.tracked_id === trackedId
          ? (previousRow || { ...da, pipeline_stage: fromStage })
          : da
      ));
    } finally {
      setLoading(false);
    }
  }, [setTracked]);

  return { updateStage, loading };
}
